// Agency Setup — Comprehensive settings hub for agency owners
// Replaces the original 3-step onboarding wizard with a full left-rail settings page.
// New agencies (no profile yet) are shown the Guided Setup Wizard first.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { csrfHeadersFor } from '../api';
import {
  Building2, Clock, Users, Shield, Zap, Globe, CreditCard,
  Code, ChevronRight, Check, AlertTriangle, X, Plus, Trash2,
  Edit3, RefreshCw, ExternalLink, UploadCloud, Calendar,
  Star, AlertCircle, Info, ChevronDown, ChevronUp, Menu, ArrowLeft,
  DollarSign
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ── Design tokens ─────────────────────────────────────────────────────────────
const INPUT_CLS = 'bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-400 w-full text-sm';
const LABEL_CLS = 'block text-xs font-medium text-gray-400 mb-1';
const BTN_PRIMARY = 'bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition-colors text-sm';
const BTN_SECONDARY = 'border border-gray-600 text-gray-300 px-4 py-2 rounded-lg hover:border-amber-400 hover:text-amber-400 transition-colors text-sm';
const BTN_DANGER = 'border border-red-800 text-red-400 px-3 py-1.5 rounded-lg hover:border-red-500 transition-colors text-xs';
const CARD_CLS = 'bg-gray-900 border border-gray-800 rounded-xl p-6';

// ── US States ─────────────────────────────────────────────────────────────────
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'
];

const CARRIER_LIST = [
  { key: 'humana',     name: 'Humana' },
  { key: 'aetna',      name: 'Aetna' },
  { key: 'anthem',     name: 'Anthem / BCBS' },
  { key: 'cigna',      name: 'Cigna' },
  { key: 'uhc',        name: 'UnitedHealthcare' },
  { key: 'devoted',    name: 'Devoted Health' },
  { key: 'caresource', name: 'CareSource' },
  { key: 'molina',     name: 'Molina Healthcare' },
  { key: 'wellcare',   name: 'WellCare' },
  { key: 'centene',    name: 'Centene' },
  { key: 'kaiser',     name: 'Kaiser Permanente' },
  { key: 'clover',     name: 'Clover Health' },
  { key: 'oscar',      name: 'Oscar Health' },
];

const SECTIONS = [
  { id: 'profile',     label: 'Agency Profile',        icon: Building2,  scoreKey: 'profileScore' },
  { id: 'hours',       label: 'Business Hours',         icon: Clock,      scoreKey: null },
  { id: 'team',        label: 'Team & Permissions',     icon: Users,      scoreKey: 'teamScore' },
  { id: 'carriers',    label: 'Carriers & Appointments',icon: Star,       scoreKey: 'carrierScore' },
  { id: 'fmo',         label: 'FMO / Upline',           icon: Globe,      scoreKey: null },
  { id: 'commissions', label: 'Commission Structures',  icon: DollarSign, scoreKey: null },
  { id: 'automations', label: 'Automations',            icon: Zap,        scoreKey: 'automationScore' },
  { id: 'compliance',  label: 'Compliance',             icon: Shield,     scoreKey: 'complianceScore' },
  { id: 'integrations',label: 'Integrations',           icon: Code,       scoreKey: null },
  { id: 'billing',     label: 'Billing & Subscription', icon: CreditCard, scoreKey: null },
];

// ── Utility ───────────────────────────────────────────────────────────────────
function ScoreBar({ value, max = 20, color = 'bg-amber-400' }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-400 tabular-nums w-10 text-right">{value}/{max}</span>
    </div>
  );
}

function SectionHeader({ title, description }) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold text-white">{title}</h2>
      {description && <p className="text-gray-400 text-sm mt-1">{description}</p>}
    </div>
  );
}

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: 'bg-emerald-900 border-emerald-700 text-emerald-300',
    error: 'bg-red-900 border-red-700 text-red-300',
    info: 'bg-blue-900 border-blue-700 text-blue-300'
  };

  return (
    <div className={`fixed top-4 right-4 z-50 border rounded-lg px-4 py-3 text-sm flex items-center gap-2 shadow-xl ${colors[type]}`}>
      {type === 'success' && <Check size={16} />}
      {type === 'error' && <AlertCircle size={16} />}
      {type === 'info' && <Info size={16} />}
      {message}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

// ── Guided Setup Wizard ────────────────────────────────────────────────────────
function SetupWizard({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  const WIZARD_STEPS = [
    { title: 'Welcome to Agency Setup', subtitle: 'Tell us a little about your agency so we can configure smart defaults.' },
    { title: 'Agency Profile',          subtitle: 'Basic info that appears on documents and client communications.' },
    { title: 'Invite Team Members',     subtitle: 'Add your agents. You can also do this later.' },
    { title: 'Your Carriers',           subtitle: 'Which carriers do you sell? We\'ll track appointments for you.' },
    { title: 'Automation Preferences',  subtitle: 'Set agency-wide defaults. Agents can override unless you lock them.' },
    { title: 'Compliance Basics',       subtitle: 'A few compliance questions to configure your workspace.' },
    { title: 'Connect Integrations',    subtitle: 'Optional — connect your calendar or messaging provider.' },
    { title: 'Setup Complete',          subtitle: 'Your Agency Health Score is ready. Here\'s what to do next.' },
  ];

  const set = (key, val) => setData(prev => ({ ...prev, [key]: val }));
  const totalSteps = WIZARD_STEPS.length;
  const pct = Math.round((step / (totalSteps - 1)) * 100);

  const handleFinish = async () => {
    setSaving(true);
    try {
      // Save profile data collected in wizard
      if (data.legal_name || data.npn || data.states_licensed) {
        await fetch(`${BASE}/api/agency-setup/profile`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
          body: JSON.stringify({
            legal_name: data.legal_name,
            npn: data.npn,
            states_licensed: data.states_licensed,
            agency_type: data.agency_type,
          })
        });
      }

      // Save wizard completed
      await fetch(`${BASE}/api/agency-setup/wizard`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify({ wizard_completed: true, wizard_step: totalSteps })
      });

      onComplete();
    } catch (err) {
      console.error('Wizard finish error:', err);
      onComplete(); // Complete even on error
    } finally {
      setSaving(false);
    }
  };

  const saveWizardStep = async (nextStep) => {
    try {
      await fetch(`${BASE}/api/agency-setup/wizard`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify({ wizard_step: nextStep })
      });
    } catch (e) { /* non-critical */ }
  };

  const next = () => {
    const ns = step + 1;
    setStep(ns);
    saveWizardStep(ns);
  };
  const back = () => setStep(step - 1);

  return (
    <div className="fixed inset-0 bg-gray-950 z-50 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Pro IRP" className="w-8 h-8 rounded-full bg-white" onError={e => e.target.style.display='none'} />
          <span className="text-white font-semibold">Agency Setup Wizard</span>
        </div>
        <button onClick={onSkip} className="text-gray-500 hover:text-gray-300 text-sm flex items-center gap-1">
          Skip wizard <X size={14} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-800">
        <div className="h-full bg-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Step counter */}
      <div className="px-6 py-3 text-xs text-gray-500">
        Step {step + 1} of {totalSteps}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white">{WIZARD_STEPS[step].title}</h1>
            <p className="text-gray-400 mt-2">{WIZARD_STEPS[step].subtitle}</p>
          </div>

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Agency size</label>
                <select className={INPUT_CLS} value={data.agency_size || ''} onChange={e => set('agency_size', e.target.value)}>
                  <option value="">Select...</option>
                  <option value="solo">Solo agent</option>
                  <option value="small">2–5 agents</option>
                  <option value="medium">6–20 agents</option>
                  <option value="large">20+ agents</option>
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Primary states you work in</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-800 border border-gray-700 rounded-lg min-h-[80px]">
                  {US_STATES.slice(0,15).map(s => (
                    <button key={s}
                      onClick={() => {
                        const cur = data.states_licensed || [];
                        set('states_licensed', cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
                      }}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        (data.states_licensed||[]).includes(s)
                          ? 'bg-amber-400 text-gray-900'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >{s}</button>
                  ))}
                  <span className="text-gray-500 text-xs self-center">+ more in Profile section</span>
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>Do you work with an FMO?</label>
                <div className="flex gap-3">
                  {['Yes','No'].map(opt => (
                    <button key={opt}
                      onClick={() => set('has_fmo', opt === 'Yes')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        (data.has_fmo === (opt === 'Yes'))
                          ? 'bg-amber-400 text-gray-900'
                          : 'bg-gray-800 border border-gray-700 text-gray-300 hover:border-amber-400'
                      }`}
                    >{opt}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Profile */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Agency / Legal Name *</label>
                <input className={INPUT_CLS} placeholder="Smith Insurance Agency" value={data.legal_name||''} onChange={e => set('legal_name', e.target.value)} />
              </div>
              <div>
                <label className={LABEL_CLS}>Agency NPN</label>
                <input className={INPUT_CLS} placeholder="12345678" value={data.npn||''} onChange={e => set('npn', e.target.value)} />
              </div>
              <div>
                <label className={LABEL_CLS}>Agency Type</label>
                <select className={INPUT_CLS} value={data.agency_type||''} onChange={e => set('agency_type', e.target.value)}>
                  <option value="">Select...</option>
                  <option value="independent">Independent Agency</option>
                  <option value="captive">Captive Agency</option>
                  <option value="fmo">FMO</option>
                  <option value="ga">General Agency (GA)</option>
                  <option value="mga">MGA</option>
                </select>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex gap-3">
                <UploadCloud size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Agency Logo</p>
                  <p className="text-xs text-gray-400">Upload in the Profile section after setup. Appears on SOAs and client emails.</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Invite team */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Team invites are sent by email. You can manage roles and permissions in the Team section.</p>
              {[0,1,2].map(i => (
                <div key={i} className="flex gap-2">
                  <input
                    className={INPUT_CLS}
                    placeholder="agent@example.com"
                    value={(data[`invite_${i}`]||'')}
                    onChange={e => set(`invite_${i}`, e.target.value)}
                  />
                  <select className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              ))}
              <p className="text-xs text-gray-500">Invites will be sent after completing setup.</p>
            </div>
          )}

          {/* Step 3: Carriers */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">Select carriers you're contracted with. Add contract numbers and expiry dates in Carriers section.</p>
              <div className="grid grid-cols-2 gap-2">
                {CARRIER_LIST.map(c => (
                  <button key={c.key}
                    onClick={() => {
                      const cur = data.carriers || [];
                      set('carriers', cur.includes(c.key) ? cur.filter(x => x !== c.key) : [...cur, c.key]);
                    }}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-colors ${
                      (data.carriers||[]).includes(c.key)
                        ? 'border-amber-400 bg-amber-400/10 text-amber-300'
                        : 'border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Check size={14} className={`flex-shrink-0 ${(data.carriers||[]).includes(c.key) ? 'opacity-100' : 'opacity-0'}`} />
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Automations */}
          {step === 4 && (
            <div className="space-y-4">
              {[
                { key: 'auto_birthday', label: 'Send birthday messages to clients', default: true },
                { key: 'auto_aep',      label: 'Send AEP enrollment period reminders', default: true },
                { key: 'auto_oep',      label: 'Send OEP reminder sequences', default: true },
                { key: 'agents_override', label: 'Allow agents to customize their own templates', default: true },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
                  <span className="text-sm text-gray-200">{item.label}</span>
                  <button
                    onClick={() => set(item.key, !(data[item.key] !== undefined ? data[item.key] : item.default))}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      (data[item.key] !== undefined ? data[item.key] : item.default) ? 'bg-amber-400' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      (data[item.key] !== undefined ? data[item.key] : item.default) ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Step 5: Compliance */}
          {step === 5 && (
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Data retention period</label>
                <select className={INPUT_CLS} value={data.retention || '7'} onChange={e => set('retention', e.target.value)}>
                  <option value="7">7 years (CMS minimum)</option>
                  <option value="10">10 years</option>
                  <option value="permanent">Permanent</option>
                </select>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
                <div>
                  <p className="text-sm text-gray-200">Require owner approval for client deletion</p>
                  <p className="text-xs text-gray-500">Prevents accidental data loss</p>
                </div>
                <button
                  onClick={() => set('require_approval', !(data.require_approval !== false))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    data.require_approval !== false ? 'bg-amber-400' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    data.require_approval !== false ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3 flex gap-2">
                <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-300">A Business Associate Agreement (BAA) is required for HIPAA compliance. Upload it in the Compliance section.</p>
              </div>
            </div>
          )}

          {/* Step 6: Integrations */}
          {step === 6 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">Connect your tools. You can configure these anytime from the Integrations section.</p>
              {[
                { name: 'Google Calendar', desc: 'Sync appointments and block busy time' },
                { name: 'Microsoft Outlook', desc: 'Sync calendar and email' },
                { name: 'Twilio', desc: 'Required for SMS automation' },
                { name: 'GoHighLevel', desc: 'CRM phone integration' },
              ].map(intg => (
                <div key={intg.name} className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-200">{intg.name}</p>
                    <p className="text-xs text-gray-500">{intg.desc}</p>
                  </div>
                  <span className="text-xs text-gray-500 italic">Configure later</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 7: Complete */}
          {step === 7 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-900/50 border-2 border-emerald-500 rounded-full flex items-center justify-center mx-auto">
                <Check size={36} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-gray-300 text-sm">Your agency workspace is configured. Your Agency Health Score will update as you complete each section.</p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-left space-y-2">
                <p className="text-sm font-semibold text-white mb-3">Recommended next steps:</p>
                {[
                  'Upload your agency logo',
                  'Add carrier contract numbers and expiry dates',
                  'Upload your BAA in Compliance',
                  'Customize your birthday message template'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-5 h-5 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-400 text-xs">{i + 1}</span>
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            {step > 0 && (
              <button onClick={back} className={BTN_SECONDARY}>Back</button>
            )}
            {step < totalSteps - 1 ? (
              <button onClick={next} className={`${BTN_PRIMARY} flex-1 flex items-center justify-center gap-2`}>
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={handleFinish} disabled={saving} className={`${BTN_PRIMARY} flex-1 flex items-center justify-center gap-2 disabled:opacity-60`}>
                {saving ? 'Finishing...' : 'Go to Agency Setup'}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Health Score Widget ────────────────────────────────────────────────────────
function HealthScoreWidget({ score, loading, onRefresh, onRelaunchWizard }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className={`${CARD_CLS} animate-pulse`}>
        <div className="h-4 bg-gray-800 rounded w-1/3 mb-4" />
        <div className="h-2 bg-gray-800 rounded w-full" />
      </div>
    );
  }

  const total = score?.totalScore ?? 0;
  const label = total >= 80 ? 'Excellent' : total >= 60 ? 'Good' : total >= 40 ? 'Fair' : 'Needs Attention';
  const labelColor = total >= 80 ? 'text-emerald-400' : total >= 60 ? 'text-amber-400' : total >= 40 ? 'text-amber-500' : 'text-red-400';
  const barColor = total >= 80 ? 'bg-emerald-400' : total >= 60 ? 'bg-amber-400' : total >= 40 ? 'bg-amber-500' : 'bg-red-400';

  const subScores = [
    { label: 'Profile', value: score?.profileScore ?? 0 },
    { label: 'Team', value: score?.teamScore ?? 0 },
    { label: 'Carriers', value: score?.carrierScore ?? 0 },
    { label: 'Automations', value: score?.automationScore ?? 0 },
    { label: 'Compliance', value: score?.complianceScore ?? 0 },
  ];

  const issues = score?.issues || [];

  return (
    <div className={CARD_CLS}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Agency Health Score</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-bold text-white tabular-nums">{total}</span>
            <span className="text-gray-600 text-lg">/100</span>
            <span className={`text-sm font-semibold ${labelColor}`}>{label}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onRefresh} className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors" title="Refresh score">
            <RefreshCw size={14} />
          </button>
          <button onClick={onRelaunchWizard} className="text-xs text-amber-400 hover:text-amber-300 transition-colors border border-amber-400/30 hover:border-amber-400 px-2 py-1 rounded">
            Relaunch Wizard
          </button>
        </div>
      </div>

      {/* Main bar */}
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-4">
        <div className={`h-full ${barColor} rounded-full transition-all duration-700`} style={{ width: `${total}%` }} />
      </div>

      {/* Sub-scores */}
      <div className="space-y-2">
        {subScores.map(s => (
          <div key={s.label}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-gray-400">{s.label}</span>
            </div>
            <ScoreBar value={s.value} max={20} color={s.value >= 16 ? 'bg-emerald-500' : s.value >= 8 ? 'bg-amber-400' : 'bg-red-500'} />
          </div>
        ))}
      </div>

      {/* Issues */}
      {issues.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            <AlertTriangle size={12} className="text-amber-400" />
            {issues.length} item{issues.length !== 1 ? 's' : ''} to improve
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1">
              {issues.map((issue, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                  <span className="text-amber-400 mt-0.5">•</span>
                  {issue.msg}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section: Profile ───────────────────────────────────────────────────────────
function ProfileSection({ onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadingAgencyLogo, setUploadingAgencyLogo] = useState(false);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  useEffect(() => {
    fetch(`${BASE}/api/agency-setup/profile`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok && j.data) setForm(j.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify(form)
      });
      const j = await res.json();
      if (j.ok) {
        setDirty(false);
        onSaved('Profile saved');
      } else {
        onSaved(j.error || 'Save failed', 'error');
      }
    } catch (err) {
      onSaved('Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAgencyLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAgencyLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await fetch(`${BASE}/api/branding/logo`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...csrfHeadersFor('POST') },
        body: formData
      });
      if (res.ok) {
        const json = await res.json();
        setForm(prev => ({ ...prev, logo_url: json.logoUrl }));
        setDirty(true);
      }
    } catch (err) {
      console.error('Agency logo upload failed:', err);
    } finally {
      setUploadingAgencyLogo(false);
    }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-800 rounded-lg" />)}</div>;

  const statesLicensed = form.states_licensed || [];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Agency Profile"
        description="Your agency's identity record. This data flows to SOA documents, automated emails, and client-facing pages."
      />

      {/* Identity */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Identity</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Agency Legal Name</label>
            <input className={INPUT_CLS} value={form.legal_name||''} onChange={e => set('legal_name', e.target.value)} placeholder="Smith Insurance Agency LLC" />
          </div>
          <div>
            <label className={LABEL_CLS}>DBA / Brand Name</label>
            <input className={INPUT_CLS} value={form.dba_name||''} onChange={e => set('dba_name', e.target.value)} placeholder="Smith Insurance" />
          </div>
          <div>
            <label className={LABEL_CLS}>Agency NPN</label>
            <input className={INPUT_CLS} value={form.npn||''} onChange={e => set('npn', e.target.value)} placeholder="12345678" />
          </div>
          <div>
            <label className={LABEL_CLS}>EIN / Tax ID</label>
            <input className={INPUT_CLS} value={form.ein||''} onChange={e => set('ein', e.target.value)} placeholder="XX-XXXXXXX" />
          </div>
          <div>
            <label className={LABEL_CLS}>Agency Type</label>
            <select className={INPUT_CLS} value={form.agency_type||''} onChange={e => set('agency_type', e.target.value)}>
              <option value="">Select...</option>
              <option value="independent">Independent Agency</option>
              <option value="captive">Captive Agency</option>
              <option value="fmo">FMO</option>
              <option value="ga">General Agency (GA)</option>
              <option value="mga">MGA</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Year Founded</label>
            <input className={INPUT_CLS} type="number" value={form.year_founded||''} onChange={e => set('year_founded', e.target.value)} placeholder="2018" min="1900" max={new Date().getFullYear()} />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Contact & Address</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Primary Phone</label>
            <input className={INPUT_CLS} value={form.primary_phone||''} onChange={e => set('primary_phone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className={LABEL_CLS}>Primary Email</label>
            <input className={INPUT_CLS} type="email" value={form.primary_email||''} onChange={e => set('primary_email', e.target.value)} placeholder="agency@example.com" />
          </div>
          <div>
            <label className={LABEL_CLS}>Website</label>
            <input className={INPUT_CLS} value={form.website||''} onChange={e => set('website', e.target.value)} placeholder="https://smithinsurance.com" />
          </div>
          <div>
            <label className={LABEL_CLS}>Street Address</label>
            <input className={INPUT_CLS} value={form.street_address||''} onChange={e => set('street_address', e.target.value)} placeholder="123 Main Street" />
          </div>
          <div>
            <label className={LABEL_CLS}>City</label>
            <input className={INPUT_CLS} value={form.city||''} onChange={e => set('city', e.target.value)} />
          </div>
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-24">
              <label className={LABEL_CLS}>State</label>
              <select className={INPUT_CLS} value={form.state||''} onChange={e => set('state', e.target.value)}>
                <option value="">--</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className={LABEL_CLS}>ZIP</label>
              <input className={INPUT_CLS} value={form.zip||''} onChange={e => set('zip', e.target.value)} maxLength={10} />
            </div>
          </div>
        </div>
      </div>

      {/* Licensed States */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-1">States Licensed In</h3>
        <p className="text-xs text-gray-500 mb-4">These states determine which carrier appointments are relevant for your agency.</p>
        <div className="flex flex-wrap gap-2">
          {US_STATES.map(s => (
            <button key={s}
              onClick={() => {
                const cur = statesLicensed;
                set('states_licensed', cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
              }}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                statesLicensed.includes(s)
                  ? 'bg-amber-400 text-gray-900'
                  : 'bg-gray-800 border border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
            >{s}</button>
          ))}
        </div>
        {statesLicensed.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">{statesLicensed.length} state{statesLicensed.length !== 1 ? 's' : ''} selected</p>
        )}
      </div>

      {/* Branding */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Branding</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Agency Logo</label>
            <div className="flex items-center gap-3">
              {form.logo_url && (
                <img src={form.logo_url} alt="Agency Logo" className="w-12 h-12 rounded object-contain bg-gray-800 border border-gray-700" />
              )}
              <label className="cursor-pointer flex items-center gap-2 border border-gray-600 text-gray-300 px-3 py-2 rounded-lg hover:border-amber-400 hover:text-amber-400 transition text-sm">
                <UploadCloud size={16} />
                {uploadingAgencyLogo ? 'Uploading...' : 'Upload Logo'}
                <input type="file" accept="image/*" className="hidden" onChange={handleAgencyLogoUpload} />
              </label>
            </div>
          </div>
          <div className="flex gap-4">
            <div>
              <label className={LABEL_CLS}>Primary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color||'#FFB800'} onChange={e => set('primary_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border-0 p-0" />
                <input className={`${INPUT_CLS} font-mono uppercase`} value={form.primary_color||'#FFB800'} onChange={e => set('primary_color', e.target.value)} maxLength={7} />
              </div>
            </div>
            <div>
              <label className={LABEL_CLS}>Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.accent_color||'#0B1120'} onChange={e => set('accent_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border-0 p-0" />
                <input className={`${INPUT_CLS} font-mono uppercase`} value={form.accent_color||'#0B1120'} onChange={e => set('accent_color', e.target.value)} maxLength={7} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About & Social */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">About & Social</h3>
        <div className="space-y-4">
          <div>
            <label className={LABEL_CLS}>Agency Bio <span className="text-gray-600">({(form.bio||'').length}/500)</span></label>
            <textarea
              className={`${INPUT_CLS} resize-none`}
              rows={4}
              maxLength={500}
              value={form.bio||''}
              onChange={e => set('bio', e.target.value)}
              placeholder="Describe your agency's specialization, history, and what sets you apart..."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={LABEL_CLS}>LinkedIn</label>
              <input className={INPUT_CLS} value={form.social_linkedin||''} onChange={e => set('social_linkedin', e.target.value)} placeholder="https://linkedin.com/company/..." />
            </div>
            <div>
              <label className={LABEL_CLS}>Facebook</label>
              <input className={INPUT_CLS} value={form.social_facebook||''} onChange={e => set('social_facebook', e.target.value)} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className={LABEL_CLS}>X / Twitter</label>
              <input className={INPUT_CLS} value={form.social_x||''} onChange={e => set('social_x', e.target.value)} placeholder="https://x.com/..." />
            </div>
          </div>
        </div>
      </div>

      {/* Public Recruiting Profile */}
      <div className={CARD_CLS}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-300">Public Recruiting Profile</h3>
            <p className="text-xs text-gray-500 mt-0.5">Allow agents searching for an agency to find and contact yours.</p>
          </div>
          <button
            onClick={() => set('public_profile_enabled', !form.public_profile_enabled)}
            className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${form.public_profile_enabled ? 'bg-amber-400' : 'bg-gray-600'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.public_profile_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {form.public_profile_enabled && (
          <div className="space-y-3">
            <div>
              <label className={LABEL_CLS}>Public Profile Slug</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">proirp.com/agencies/</span>
                <input className={INPUT_CLS} value={form.public_profile_slug||''} onChange={e => set('public_profile_slug', e.target.value)} placeholder="smith-insurance" />
              </div>
            </div>
            <div>
              <label className={LABEL_CLS}>Recruiting / Contact URL</label>
              <input className={INPUT_CLS} value={form.recruiting_url||''} onChange={e => set('recruiting_url', e.target.value)} placeholder="https://..." />
            </div>
          </div>
        )}
      </div>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-gray-950 border-t border-gray-800 p-4 flex justify-end gap-3 -mx-6 -mb-6 px-6">
        {dirty && <span className="text-xs text-amber-400 self-center">Unsaved changes</span>}
        <button onClick={save} disabled={saving || !dirty} className={`${BTN_PRIMARY} disabled:opacity-50`}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}

// ── Section: Business Hours ────────────────────────────────────────────────────
function BusinessHoursSection({ onSaved }) {
  const [hours, setHours] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', label: '', recurring: true });

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/agency-setup/business-hours`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${BASE}/api/agency-setup/holidays`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([hoursJ, holidaysJ]) => {
      if (hoursJ.ok) setHours(hoursJ.data || []);
      if (holidaysJ.ok) setHolidays(holidaysJ.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const updateDay = (dayIdx, key, val) => {
    setHours(prev => prev.map(h => h.day_of_week === dayIdx ? { ...h, [key]: val } : h));
  };

  const copyMondayToWeekdays = () => {
    const monday = hours.find(h => h.day_of_week === 1);
    if (!monday) return;
    setHours(prev => prev.map(h =>
      [1,2,3,4,5].includes(h.day_of_week)
        ? { ...h, is_open: monday.is_open, open_time: monday.open_time, close_time: monday.close_time }
        : h
    ));
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/business-hours`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify({ hours })
      });
      const j = await res.json();
      if (j.ok) onSaved('Business hours saved');
      else onSaved(j.error || 'Save failed', 'error');
    } catch (err) {
      onSaved('Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.date) return;
    try {
      const res = await fetch(`${BASE}/api/agency-setup/holidays`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify(newHoliday)
      });
      const j = await res.json();
      if (j.ok) {
        setHolidays(prev => [...prev, j.data]);
        setNewHoliday({ date: '', label: '', recurring: true });
        onSaved('Holiday added');
      }
    } catch (err) {
      onSaved('Failed to add holiday', 'error');
    }
  };

  const deleteHoliday = async (id) => {
    try {
      await fetch(`${BASE}/api/agency-setup/holidays/${id}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
      setHolidays(prev => prev.filter(h => h.id !== id));
    } catch (e) { /* silent */ }
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-800 rounded-xl" />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Business Hours"
        description="Controls when automated messages fire and what clients see as your office hours."
      />

      <div className={CARD_CLS}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">Weekly Schedule</h3>
          <button onClick={copyMondayToWeekdays} className={BTN_SECONDARY}>Copy Mon to Weekdays</button>
        </div>
        <div className="space-y-3">
          {DAYS.map((day, idx) => {
            const h = hours.find(x => x.day_of_week === idx) || { day_of_week: idx, is_open: false, open_time: '09:00', close_time: '17:00' };
            return (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-24 flex items-center gap-2">
                  <button
                    onClick={() => updateDay(idx, 'is_open', !h.is_open)}
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${h.is_open ? 'bg-amber-400' : 'bg-gray-600'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${h.is_open ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                  <span className={`text-sm ${h.is_open ? 'text-gray-200' : 'text-gray-600'}`}>{day.slice(0,3)}</span>
                </div>
                {h.is_open ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input type="time" value={h.open_time||'09:00'} onChange={e => updateDay(idx, 'open_time', e.target.value)}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-400" />
                    <span className="text-gray-600">to</span>
                    <input type="time" value={h.close_time||'17:00'} onChange={e => updateDay(idx, 'close_time', e.target.value)}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-400" />
                  </div>
                ) : (
                  <span className="text-gray-600 text-sm italic">Closed</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-800">
          <label className={LABEL_CLS}>Timezone</label>
          <select
            className={`${INPUT_CLS} max-w-xs`}
            value={hours[0]?.timezone || 'America/New_York'}
            onChange={e => setHours(prev => prev.map(h => ({ ...h, timezone: e.target.value })))}
          >
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      {/* After-hours messages */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">After-Hours Auto-Reply</h3>
        <div className="space-y-4">
          <div>
            <label className={LABEL_CLS}>SMS Auto-Reply <span className="text-gray-600">({(hours[0]?.after_hours_sms||'').length}/160)</span></label>
            <textarea
              className={`${INPUT_CLS} resize-none`}
              rows={2}
              maxLength={160}
              value={hours[0]?.after_hours_sms||''}
              onChange={e => setHours(prev => prev.map(h => ({ ...h, after_hours_sms: e.target.value })))}
              placeholder="Thanks for contacting {{agency_name}}. We'll get back to you on the next business day."
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Email Auto-Reply</label>
            <textarea
              className={`${INPUT_CLS} resize-none`}
              rows={3}
              value={hours[0]?.after_hours_email||''}
              onChange={e => setHours(prev => prev.map(h => ({ ...h, after_hours_email: e.target.value })))}
              placeholder="Thank you for reaching out. Our office is currently closed. We will respond on the next business day."
            />
          </div>
        </div>
      </div>

      {/* Holidays */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Holiday Schedule</h3>
        <div className="space-y-2 mb-4">
          {holidays.length === 0 && (
            <p className="text-gray-500 text-sm italic">No holidays configured.</p>
          )}
          {holidays.map(h => (
            <div key={h.id} className="flex items-center justify-between py-2 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <Calendar size={14} className="text-gray-500" />
                <span className="text-sm text-gray-200">{h.label || 'Holiday'}</span>
                <span className="text-xs text-gray-500 tabular-nums">{h.holiday_date}</span>
                {h.recurring && <span className="text-xs text-amber-400/70 border border-amber-400/30 px-1.5 py-0.5 rounded">Recurring</span>}
              </div>
              <button onClick={() => deleteHoliday(h.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className={LABEL_CLS}>Date</label>
            <input type="date" className={INPUT_CLS} value={newHoliday.date} onChange={e => setNewHoliday(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div className="flex-1">
            <label className={LABEL_CLS}>Label</label>
            <input className={INPUT_CLS} placeholder="Christmas" value={newHoliday.label} onChange={e => setNewHoliday(p => ({ ...p, label: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <input type="checkbox" id="recurring" checked={newHoliday.recurring} onChange={e => setNewHoliday(p => ({ ...p, recurring: e.target.checked }))} className="accent-amber-400" />
            <label htmlFor="recurring" className="text-xs text-gray-400">Recurring</label>
          </div>
          <button onClick={addHoliday} className={`${BTN_PRIMARY} flex-shrink-0`}>Add</button>
        </div>
      </div>

      <div className="sticky bottom-0 bg-gray-950 border-t border-gray-800 p-4 flex justify-end -mx-6 -mb-6 px-6">
        <button onClick={saveHours} disabled={saving} className={`${BTN_PRIMARY} disabled:opacity-50`}>
          {saving ? 'Saving...' : 'Save Hours'}
        </button>
      </div>
    </div>
  );
}

// ── Section: Team ─────────────────────────────────────────────────────────────
function TeamSection({ onSaved }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [agencyPermissions, setAgencyPermissions] = useState({ allow_agent_branding: true, allow_agent_prospecting: true });

  const load = useCallback(() => {
    fetch(`${BASE}/api/agency-setup/team`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok) setMembers(j.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Load agency permissions
    fetch(`${BASE}/api/agency-setup/profile`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => {
        if (j.ok && j.data) {
          setAgencyPermissions({
            allow_agent_branding: j.data.allow_agent_branding ?? true,
            allow_agent_prospecting: j.data.allow_agent_prospecting ?? true,
          });
        }
      })
      .catch(() => {});
  }, [load]);

  const saveAgencyPermission = async (key, value) => {
    try {
      setAgencyPermissions(prev => ({ ...prev, [key]: value }));
      await fetch(`${BASE}/api/agency-setup/permissions`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PATCH') },
        body: JSON.stringify({ [key]: value })
      });
    } catch (err) {
      console.error('Failed to save permission:', err);
    }
  };

  const handleRoleChange = async (memberId, role) => {
    setActionLoading(memberId);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/team/${memberId}/role`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PATCH') },
        body: JSON.stringify({ role })
      });
      const j = await res.json();
      if (j.ok) { onSaved('Role updated'); load(); }
      else onSaved(j.error || 'Update failed', 'error');
    } catch (e) {
      onSaved('Update failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (memberId) => {
    if (!window.confirm('Suspend this member? They will lose access immediately.')) return;
    setActionLoading(memberId);
    try {
      await fetch(`${BASE}/api/agency-setup/team/${memberId}/suspend`, { method: 'PATCH', credentials: 'include', headers: { ...csrfHeadersFor('PATCH') } });
      onSaved('Member suspended');
      load();
    } catch (e) {
      onSaved('Action failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (memberId) => {
    setActionLoading(memberId);
    try {
      await fetch(`${BASE}/api/agency-setup/team/${memberId}/reactivate`, { method: 'PATCH', credentials: 'include', headers: { ...csrfHeadersFor('PATCH') } });
      onSaved('Member reactivated');
      load();
    } catch (e) {
      onSaved('Action failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: 'bg-emerald-900/50 text-emerald-400 border-emerald-800',
      suspended: 'bg-red-900/50 text-red-400 border-red-800',
      pending: 'bg-amber-900/50 text-amber-400 border-amber-800',
    };
    return `text-xs px-2 py-0.5 rounded border ${map[status] || map.pending}`;
  };

  const roleBadge = (role) => {
    const map = {
      owner: 'text-amber-400',
      admin: 'text-blue-400',
      manager: 'text-purple-400',
      agent: 'text-gray-400',
      read_only: 'text-gray-500',
    };
    return map[role] || 'text-gray-400';
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-800 rounded-xl" />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Team & Permissions"
        description="Manage your agency members, roles, and access levels."
      />

      <div className={CARD_CLS}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">{members.length} Member{members.length !== 1 ? 's' : ''}</h3>
        </div>

        {members.length === 0 && (
          <div className="text-center py-8">
            <Users size={32} className="mx-auto text-gray-700 mb-2" />
            <p className="text-gray-500 text-sm">No team members yet.</p>
          </div>
        )}

        <div className="space-y-0 divide-y divide-gray-800">
          {members.map(m => (
            <div key={m.member_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-8 h-8 rounded-full bg-amber-400/20 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 text-xs font-bold">
                    {(m.first_name?.[0] || '?').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-200">{m.first_name} {m.last_name}</p>
                  <p className="text-xs text-gray-500">{m.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                {/* NPN status */}
                {m.npn ? (
                  <span className="text-xs text-emerald-400 flex items-center gap-1"><Check size={10} />NPN</span>
                ) : (
                  <span className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle size={10} />No NPN</span>
                )}

                {/* Appointment warnings */}
                {m.expiring_soon_count > 0 && (
                  <span className="text-xs text-amber-400">{m.expiring_soon_count} expiring</span>
                )}

                {/* Role selector */}
                <select
                  value={m.member_role}
                  onChange={e => handleRoleChange(m.member_id, e.target.value)}
                  disabled={actionLoading === m.member_id}
                  className={`bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 ${roleBadge(m.member_role)}`}
                >
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="agent">Agent</option>
                  <option value="read_only">Read Only</option>
                  <option value="billing_admin">Billing Admin</option>
                </select>

                {/* Status badge */}
                <span className={statusBadge(m.member_status)}>{m.member_status}</span>

                {/* Actions */}
                {m.member_status === 'active' ? (
                  <button onClick={() => handleSuspend(m.member_id)} className={BTN_DANGER} disabled={actionLoading === m.member_id}>
                    Suspend
                  </button>
                ) : m.member_status === 'suspended' ? (
                  <button onClick={() => handleReactivate(m.member_id)} className="border border-emerald-800 text-emerald-400 px-3 py-1.5 rounded-lg hover:border-emerald-600 transition-colors text-xs" disabled={actionLoading === m.member_id}>
                    Reactivate
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Role permissions reference */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Role Permissions Reference</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-500 py-2 pr-4 font-medium">Permission</th>
                {['Owner','Admin','Manager','Agent','Read Only'].map(r => (
                  <th key={r} className="text-center text-gray-500 py-2 px-3 font-medium">{r}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {[
                ['View all clients', true, true, true, false, true],
                ['Edit clients / PHI', true, true, true, false, false],
                ['Export client data', true, true, false, false, false],
                ['Send SMS / Email', true, true, true, true, false],
                ['View all comms threads', true, true, true, false, true],
                ['Manage team members', true, true, false, false, false],
                ['Edit agency settings', true, true, false, false, false],
                ['Manage billing', true, false, false, false, false],
                ['View SOAs', true, true, true, true, true],
                ['Generate SOAs', true, true, true, true, false],
                ['View audit log', true, true, true, false, false],
              ].map(([label, ...perms]) => (
                <tr key={label}>
                  <td className="text-gray-400 py-2 pr-4">{label}</td>
                  {perms.map((p, i) => (
                    <td key={i} className="text-center py-2 px-3">
                      {p ? <Check size={12} className="mx-auto text-emerald-400" /> : <X size={12} className="mx-auto text-gray-700" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agent Feature Permissions */}
      <div className={CARD_CLS}>
        <h3 className="text-white font-semibold mb-4">Agent Feature Permissions</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-white text-sm font-medium">Agent Custom Branding</div>
              <div className="text-gray-400 text-xs">Allow agents to upload their own logo and set brand colors</div>
            </div>
            <input type="checkbox" className="w-5 h-5 accent-amber-400 cursor-pointer"
              checked={agencyPermissions.allow_agent_branding !== false}
              onChange={(e) => saveAgencyPermission('allow_agent_branding', e.target.checked)} />
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-white text-sm font-medium">Prospecting Pipeline</div>
              <div className="text-gray-400 text-xs">Allow agents to use the full prospecting pipeline and campaigns</div>
            </div>
            <input type="checkbox" className="w-5 h-5 accent-amber-400 cursor-pointer"
              checked={agencyPermissions.allow_agent_prospecting !== false}
              onChange={(e) => saveAgencyPermission('allow_agent_prospecting', e.target.checked)} />
          </label>
        </div>
      </div>
    </div>
  );
}

// ── Section: Carriers ─────────────────────────────────────────────────────────
function CarriersSection({ onSaved }) {
  const [carriers, setCarriers] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCarrier, setNewCarrier] = useState({ carrier_key: '', carrier_name: '', contract_number: '', effective_date: '', expiry_date: '', hierarchy_level: 'direct', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      fetch(`${BASE}/api/agency-setup/carriers`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${BASE}/api/agency-setup/carriers/expiring`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([carriersJ, expiringJ]) => {
      if (carriersJ.ok) setCarriers(carriersJ.data || []);
      if (expiringJ.ok) setExpiring(expiringJ.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setNew = (key, val) => setNewCarrier(prev => ({ ...prev, [key]: val }));

  const addCarrier = async () => {
    if (!newCarrier.carrier_key) return;
    setSaving(true);
    try {
      const payload = { ...newCarrier };
      if (!payload.carrier_name) {
        payload.carrier_name = CARRIER_LIST.find(c => c.key === payload.carrier_key)?.name || payload.carrier_key;
      }
      const res = await fetch(`${BASE}/api/agency-setup/carriers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify(payload)
      });
      const j = await res.json();
      if (j.ok) {
        setShowAddForm(false);
        setNewCarrier({ carrier_key: '', carrier_name: '', contract_number: '', effective_date: '', expiry_date: '', hierarchy_level: 'direct', notes: '' });
        onSaved('Carrier added');
        load();
      } else {
        onSaved(j.error || 'Add failed', 'error');
      }
    } catch (e) {
      onSaved('Add failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCarrier = async (id) => {
    if (!window.confirm('Remove this carrier?')) return;
    try {
      await fetch(`${BASE}/api/agency-setup/carriers/${id}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
      onSaved('Carrier removed');
      load();
    } catch (e) {
      onSaved('Remove failed', 'error');
    }
  };

  const urgencyStyle = (u) => {
    if (u === 'expired') return 'text-red-400 border-red-800 bg-red-900/20';
    if (u === 'critical') return 'text-red-400 border-red-800 bg-red-900/10';
    if (u === 'warning') return 'text-amber-400 border-amber-800 bg-amber-900/10';
    return 'text-emerald-400 border-emerald-800';
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-800 rounded-xl" />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Carriers & Appointments"
        description="Manage your agency carrier contracts and track appointment expiry for every agent."
      />

      {/* Appointment health summary */}
      {expiring.length > 0 && (
        <div className="bg-amber-900/20 border border-amber-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-amber-300">Appointment Health Alert</h3>
          </div>
          <div className="space-y-2">
            {expiring.slice(0, 5).map(e => (
              <div key={e.id} className={`flex items-center justify-between p-2 rounded-lg border text-xs ${urgencyStyle(e.urgency)}`}>
                <span className="font-medium">{e.carrier_name}</span>
                <span>{e.user_id ? `${e.first_name} ${e.last_name}` : 'Agency'}</span>
                <span className="tabular-nums">
                  {e.urgency === 'expired' ? 'EXPIRED' : `${Math.abs(e.days_until_expiry)}d`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carrier cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {carriers.map(c => {
          const isExpired = c.expiry_date && new Date(c.expiry_date) < new Date();
          const isExpiringSoon = c.expiry_date && !isExpired && (new Date(c.expiry_date) - new Date()) / (1000 * 60 * 60 * 24) <= 60;

          return (
            <div key={c.id} className={`${CARD_CLS} relative`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-white">{c.carrier_name}</h3>
                  {c.contract_number && <p className="text-xs text-gray-500 tabular-nums">Contract: {c.contract_number}</p>}
                </div>
                <div className="flex gap-2">
                  {isExpired && <span className="text-xs px-2 py-0.5 rounded bg-red-900/50 text-red-400 border border-red-800">Expired</span>}
                  {isExpiringSoon && <span className="text-xs px-2 py-0.5 rounded bg-amber-900/50 text-amber-400 border border-amber-800">Expiring</span>}
                  {!isExpired && !isExpiringSoon && c.expiry_date && <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/50 text-emerald-400 border border-emerald-800">Active</span>}
                </div>
              </div>

              <div className="space-y-1 text-xs text-gray-400">
                {c.effective_date && <p>Effective: <span className="text-gray-300 tabular-nums">{c.effective_date}</span></p>}
                {c.expiry_date && <p>Expires: <span className={`tabular-nums ${isExpired ? 'text-red-400' : isExpiringSoon ? 'text-amber-400' : 'text-gray-300'}`}>{c.expiry_date}</span></p>}
                <p>Level: <span className="text-gray-300 capitalize">{c.hierarchy_level}</span></p>
                <p>Agents appointed: <span className="text-gray-300 tabular-nums">{c.agents_appointed}/{c.total_agents}</span></p>
              </div>

              {c.portal_url && (
                <a href={c.portal_url} target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                  Carrier portal <ExternalLink size={10} />
                </a>
              )}

              <button onClick={() => deleteCarrier(c.id)} className="absolute top-3 right-3 text-gray-700 hover:text-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}

        {/* Add carrier button */}
        <button
          onClick={() => setShowAddForm(true)}
          className="border-2 border-dashed border-gray-700 hover:border-amber-400/50 rounded-xl p-6 flex flex-col items-center gap-2 text-gray-500 hover:text-amber-400 transition-colors"
        >
          <Plus size={24} />
          <span className="text-sm font-medium">Add Carrier</span>
        </button>
      </div>

      {/* Add carrier form */}
      {showAddForm && (
        <div className={CARD_CLS}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300">Add Carrier Contract</h3>
            <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-300"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLS}>Carrier *</label>
              <select className={INPUT_CLS} value={newCarrier.carrier_key} onChange={e => {
                const found = CARRIER_LIST.find(c => c.key === e.target.value);
                setNew('carrier_key', e.target.value);
                setNew('carrier_name', found?.name || '');
              }}>
                <option value="">Select carrier...</option>
                {CARRIER_LIST.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                <option value="other">Other / Custom</option>
              </select>
            </div>
            {newCarrier.carrier_key === 'other' && (
              <div>
                <label className={LABEL_CLS}>Carrier Name *</label>
                <input className={INPUT_CLS} value={newCarrier.carrier_name} onChange={e => setNew('carrier_name', e.target.value)} placeholder="Carrier name" />
              </div>
            )}
            <div>
              <label className={LABEL_CLS}>Contract Number</label>
              <input className={INPUT_CLS} value={newCarrier.contract_number} onChange={e => setNew('contract_number', e.target.value)} placeholder="H1234-56" />
            </div>
            <div>
              <label className={LABEL_CLS}>Effective Date</label>
              <input type="date" className={INPUT_CLS} value={newCarrier.effective_date} onChange={e => setNew('effective_date', e.target.value)} />
            </div>
            <div>
              <label className={LABEL_CLS}>Expiry Date</label>
              <input type="date" className={INPUT_CLS} value={newCarrier.expiry_date} onChange={e => setNew('expiry_date', e.target.value)} />
            </div>
            <div>
              <label className={LABEL_CLS}>Hierarchy Level</label>
              <select className={INPUT_CLS} value={newCarrier.hierarchy_level} onChange={e => setNew('hierarchy_level', e.target.value)}>
                <option value="direct">Direct with Carrier</option>
                <option value="fmo">Through FMO</option>
                <option value="ga">Through GA</option>
                <option value="mga">Through MGA</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL_CLS}>Notes</label>
              <input className={INPUT_CLS} value={newCarrier.notes} onChange={e => setNew('notes', e.target.value)} placeholder="Optional notes..." />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowAddForm(false)} className={BTN_SECONDARY}>Cancel</button>
            <button onClick={addCarrier} disabled={saving || !newCarrier.carrier_key} className={`${BTN_PRIMARY} disabled:opacity-50`}>
              {saving ? 'Adding...' : 'Add Carrier'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: FMO ──────────────────────────────────────────────────────────────
function FMOSection({ onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setDirty(true); };

  useEffect(() => {
    fetch(`${BASE}/api/agency-setup/fmo`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok && j.data) setForm(j.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/fmo`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify(form)
      });
      const j = await res.json();
      if (j.ok) { setDirty(false); onSaved('FMO info saved'); }
      else onSaved(j.error || 'Save failed', 'error');
    } catch (e) { onSaved('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-800 rounded-xl" />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="FMO / Upline Management"
        description="Document your agency's hierarchy for compliance, commission tracking, and FMO relationship management."
      />

      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">FMO / Upline Relationship</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Relationship Type</label>
            <select className={INPUT_CLS} value={form.relationship_type||'fmo'} onChange={e => set('relationship_type', e.target.value)}>
              <option value="direct">Direct with Carriers</option>
              <option value="fmo">Through FMO</option>
              <option value="ga">Through GA</option>
              <option value="mga">Through MGA</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>FMO / IMO Name</label>
            <input className={INPUT_CLS} value={form.fmo_name||''} onChange={e => set('fmo_name', e.target.value)} placeholder="National Health Partners" />
          </div>
          <div>
            <label className={LABEL_CLS}>FMO NPN</label>
            <input className={INPUT_CLS} value={form.fmo_npn||''} onChange={e => set('fmo_npn', e.target.value)} placeholder="87654321" />
          </div>
          <div>
            <label className={LABEL_CLS}>FMO Portal URL</label>
            <input className={INPUT_CLS} value={form.fmo_portal_url||''} onChange={e => set('fmo_portal_url', e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className={LABEL_CLS}>Upline Agent / Agency Name</label>
            <input className={INPUT_CLS} value={form.upline_name||''} onChange={e => set('upline_name', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_CLS}>Upline NPN</label>
            <input className={INPUT_CLS} value={form.upline_npn||''} onChange={e => set('upline_npn', e.target.value)} />
          </div>
        </div>
      </div>

      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Compliance Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLS}>Contact Name</label>
            <input className={INPUT_CLS} value={form.compliance_contact_name||''} onChange={e => set('compliance_contact_name', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_CLS}>Contact Email</label>
            <input type="email" className={INPUT_CLS} value={form.compliance_contact_email||''} onChange={e => set('compliance_contact_email', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_CLS}>Contact Phone</label>
            <input className={INPUT_CLS} value={form.compliance_contact_phone||''} onChange={e => set('compliance_contact_phone', e.target.value)} />
          </div>
        </div>
      </div>

      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Notes</h3>
        <textarea
          className={`${INPUT_CLS} resize-none`}
          rows={4}
          value={form.notes||''}
          onChange={e => set('notes', e.target.value)}
          placeholder="e.g. This FMO handles Humana and Aetna but not Cigna. Commission overrides are on file separately."
        />
      </div>

      <div className="sticky bottom-0 bg-gray-950 border-t border-gray-800 p-4 flex justify-end gap-3 -mx-6 -mb-6 px-6">
        {dirty && <span className="text-xs text-amber-400 self-center">Unsaved changes</span>}
        <button onClick={save} disabled={saving || !dirty} className={`${BTN_PRIMARY} disabled:opacity-50`}>
          {saving ? 'Saving...' : 'Save FMO Info'}
        </button>
      </div>
    </div>
  );
}

// ── Section: Automations (read-only + links to existing pages) ────────────────
function AutomationsSection() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <SectionHeader
        title="Automations & Templates"
        description="Agency-wide automation defaults. Configure templates and schedules — individual agents can override unless locked."
      />

      <div className={CARD_CLS}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">Automation Configuration</h3>
          <button onClick={() => navigate('/automations')} className={BTN_PRIMARY}>
            Open Automations
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Your automation settings — birthday messages, AEP reminders, OEP sequences — are managed in the Automations module where you can also customize templates per-agent.
        </p>
        <div className="space-y-3">
          {[
            { label: 'Birthday SMS', desc: 'Sent on client birthday' },
            { label: 'AEP Reminder Series', desc: 'Oct 15 – Dec 7 enrollment period' },
            { label: 'OEP Reminder Sequence', desc: 'Jan 1 – Mar 31 open enrollment' },
            { label: 'Annual Review Invite', desc: 'Triggered 11 months after effective date' },
          ].map(a => (
            <div key={a.label} className="flex items-center gap-3 p-3 bg-gray-800 border border-gray-700 rounded-lg">
              <Zap size={14} className="text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-gray-200">{a.label}</p>
                <p className="text-xs text-gray-500">{a.desc}</p>
              </div>
              <ExternalLink size={12} className="text-gray-600" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4 flex gap-3">
        <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-300">Scheduling Windows</p>
          <p className="text-xs text-blue-400 mt-1">
            All automated messages respect your Business Hours settings. Configure the allowed send window (e.g., 9 AM – 6 PM recipient local time) in the Automations module.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Section: Compliance ───────────────────────────────────────────────────────
function ComplianceSection({ onSaved }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setDirty(true); };

  useEffect(() => {
    fetch(`${BASE}/api/agency-setup/compliance`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok) setForm(j.data || {}); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/compliance`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify(form)
      });
      const j = await res.json();
      if (j.ok) { setDirty(false); onSaved('Compliance settings saved'); }
      else onSaved(j.error || 'Save failed', 'error');
    } catch (e) { onSaved('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-800 rounded-xl" />;

  const Toggle = ({ field, label, description }) => (
    <div className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
      <div>
        <p className="text-sm text-gray-200">{label}</p>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <button
        onClick={() => set(field, !form[field])}
        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${form[field] ? 'bg-amber-400' : 'bg-gray-600'}`}
      >
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form[field] ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Compliance Settings"
        description="HIPAA configuration, data retention, SOA settings, and your audit log."
      />

      {/* BAA */}
      <div className={CARD_CLS}>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-sm font-semibold text-gray-300">Business Associate Agreement (BAA)</h3>
          {form.baa_on_file
            ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/50 text-emerald-400 border border-emerald-800">On File</span>
            : <span className="text-xs px-2 py-0.5 rounded bg-red-900/50 text-red-400 border border-red-800">Missing</span>
          }
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>BAA Effective Date</label>
            <input type="date" className={INPUT_CLS} value={form.baa_effective_date||''} onChange={e => set('baa_effective_date', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_CLS}>Counterparty / Signing Entity</label>
            <input className={INPUT_CLS} value={form.baa_counterparty||''} onChange={e => set('baa_counterparty', e.target.value)} placeholder="Pro IRP Inc." />
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL_CLS}>BAA File URL (PDF)</label>
            <input className={INPUT_CLS} value={form.baa_file_url||''} onChange={e => set('baa_file_url', e.target.value)} placeholder="https://..." />
          </div>
        </div>
        <div className="mt-3">
          <Toggle field="baa_on_file" label="Mark BAA as on file" description="Affects your Agency Health Score" />
        </div>
      </div>

      {/* Data Retention */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Data Retention</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLS}>Client data retention</label>
            <select className={INPUT_CLS} value={form.data_retention_years||7} onChange={e => set('data_retention_years', parseInt(e.target.value))}>
              <option value={7}>7 years (CMS minimum)</option>
              <option value={10}>10 years</option>
              <option value={99}>Permanent</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Communications retention</label>
            <select className={INPUT_CLS} value={form.comms_retention_years||7} onChange={e => set('comms_retention_years', parseInt(e.target.value))}>
              <option value={7}>7 years</option>
              <option value={10}>10 years</option>
              <option value={99}>Permanent</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Auto-archive inactive clients after</label>
            <select className={INPUT_CLS} value={form.archive_after_months||24} onChange={e => set('archive_after_months', parseInt(e.target.value))}>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
              <option value={0}>Never</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Toggle
            field="require_owner_deletion_approval"
            label="Require owner approval for permanent client deletion"
            description="Recommended to prevent accidental data loss"
          />
        </div>
      </div>

      {/* SOA Settings */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">SOA Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLS}>Signature Method</label>
            <select className={INPUT_CLS} value={form.soa_signature_method||'digital'} onChange={e => set('soa_signature_method', e.target.value)}>
              <option value="digital">Digital Signature</option>
              <option value="wet">Wet Signature</option>
              <option value="phone">Phone Attestation</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>SOA valid window (hours)</label>
            <input type="number" className={INPUT_CLS} value={form.soa_valid_hours||48} onChange={e => set('soa_valid_hours', parseInt(e.target.value))} min={48} />
          </div>
          <div>
            <label className={LABEL_CLS}>Auto-reminder (hours before appt)</label>
            <input type="number" className={INPUT_CLS} value={form.soa_reminder_hours||24} onChange={e => set('soa_reminder_hours', parseInt(e.target.value))} min={1} />
          </div>
        </div>
        <div className="mt-3">
          <Toggle field="soa_configured" label="Mark SOA settings as configured" description="Affects your Agency Health Score" />
        </div>
      </div>

      {/* HIPAA */}
      <div className={CARD_CLS}>
        <h3 className="text-sm font-semibold text-gray-300 mb-4">HIPAA & Security</h3>
        <div className="space-y-3">
          <div>
            <label className={LABEL_CLS}>MFA Enforcement</label>
            <select className={INPUT_CLS} value={form.mfa_enforcement||'optional'} onChange={e => set('mfa_enforcement', e.target.value)}>
              <option value="optional">Optional</option>
              <option value="admins">Required for Admins and Owners</option>
              <option value="all">Required for All Users</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Session timeout (minutes idle)</label>
            <select className={INPUT_CLS} value={form.session_timeout_minutes||30} onChange={e => set('session_timeout_minutes', parseInt(e.target.value))}>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={120}>2 hours</option>
            </select>
          </div>
          <Toggle field="audit_log_enabled" label="Enable audit log" description="Records all settings changes and sensitive actions" />
          <Toggle field="phi_access_log_enabled" label="Enable PHI access logging" description="Logs every view of PHI fields (DOB, SSN, Medicare ID)" />
        </div>
      </div>

      <div className="sticky bottom-0 bg-gray-950 border-t border-gray-800 p-4 flex justify-end gap-3 -mx-6 -mb-6 px-6">
        {dirty && <span className="text-xs text-amber-400 self-center">Unsaved changes</span>}
        <button onClick={save} disabled={saving || !dirty} className={`${BTN_PRIMARY} disabled:opacity-50`}>
          {saving ? 'Saving...' : 'Save Compliance Settings'}
        </button>
      </div>
    </div>
  );
}

// ── Section: Integrations (display only — links to existing modules) ───────────
function IntegrationsSection() {
  const integrations = [
    { name: 'Cigna', type: 'carrier', status: 'connected', desc: 'FHIR patient access' },
    { name: 'Aetna', type: 'carrier', status: 'connected', desc: 'FHIR patient access (sandbox)' },
    { name: 'Anthem / BCBS', type: 'carrier', status: 'connected', desc: 'FHIR via CareEvolution (sandbox)' },
    { name: 'Devoted Health', type: 'carrier', status: 'connected', desc: 'FHIR patient access' },
    { name: 'CareSource', type: 'carrier', status: 'connected', desc: 'FHIR patient access' },
    { name: 'Humana', type: 'carrier', status: 'pending', desc: 'No credentials deployed' },
    { name: 'UnitedHealthcare', type: 'carrier', status: 'waiting', desc: 'Awaiting attestation review' },
    { name: 'WellCare', type: 'carrier', status: 'waiting', desc: 'Awaiting app approval' },
    { name: 'Twilio', type: 'comms', status: 'external', desc: 'SMS sending & receiving' },
    { name: 'GoHighLevel', type: 'comms', status: 'external', desc: 'CRM phone integration' },
    { name: 'Google Calendar', type: 'calendar', status: 'external', desc: 'Appointment sync' },
  ];

  const statusStyle = (s) => {
    if (s === 'connected') return 'bg-emerald-900/50 text-emerald-400 border-emerald-800';
    if (s === 'pending') return 'bg-gray-800 text-gray-400 border-gray-700';
    if (s === 'waiting') return 'bg-amber-900/30 text-amber-500 border-amber-900';
    if (s === 'external') return 'bg-blue-900/30 text-blue-400 border-blue-900';
    return 'bg-gray-800 text-gray-500 border-gray-700';
  };

  const statusLabel = (s) => {
    if (s === 'connected') return 'Active';
    if (s === 'pending') return 'No credentials';
    if (s === 'waiting') return 'Pending approval';
    if (s === 'external') return 'See settings';
    return s;
  };

  const grouped = {
    carrier: integrations.filter(i => i.type === 'carrier'),
    comms: integrations.filter(i => i.type === 'comms'),
    calendar: integrations.filter(i => i.type === 'calendar'),
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Integrations"
        description="Connected carrier data sources, communication providers, and calendars."
      />

      {Object.entries({ 'Carrier Data Integrations': grouped.carrier, 'Communications': grouped.comms, 'Calendar': grouped.calendar }).map(([group, items]) => (
        <div key={group} className={CARD_CLS}>
          <h3 className="text-sm font-semibold text-gray-300 mb-4">{group}</h3>
          <div className="space-y-2">
            {items.map(intg => (
              <div key={intg.name} className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
                <div>
                  <p className="text-sm text-gray-200">{intg.name}</p>
                  <p className="text-xs text-gray-500">{intg.desc}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border ${statusStyle(intg.status)}`}>
                  {statusLabel(intg.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4 flex gap-3">
        <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-300">
          Carrier OAuth credentials, Twilio config, and GHL settings are managed via environment variables on the backend. Contact your Pro IRP administrator to update credentials.
        </p>
      </div>
    </div>
  );
}

// ── Section: Billing (read-only display) ──────────────────────────────────────
function BillingSection() {
  return (
    <div className="space-y-6">
      <SectionHeader
        title="Billing & Subscription"
        description="Your current plan, usage, and payment information."
      />
      <div className={CARD_CLS}>
        <div className="text-center py-8">
          <CreditCard size={32} className="mx-auto text-gray-700 mb-3" />
          <p className="text-gray-400 text-sm">Billing management is handled separately.</p>
          <p className="text-gray-500 text-xs mt-1">Contact support@proirp.com to update your plan or payment method.</p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMMISSION STRUCTURES
// ═══════════════════════════════════════════════════════════════════════════════

const CMS_DEFAULTS_2026 = {
  ma:  { initial: 694, renewal: 347 },
  pdp: { initial: 114, renewal: 57  },
};

const PRODUCT_TABS = [
  { key: 'ma',     label: 'Medicare Advantage' },
  { key: 'pdp',    label: 'Part D (PDP)' },
  { key: 'medsup', label: 'Med Supplement' },
];

const OVERRIDE_TIERS = ['fmo','sga','mga','ga'];

// ── Revenue Summary Bar ───────────────────────────────────────────────────────
function RevenueSummaryBar({ refreshKey }) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE}/api/agency-setup/commissions/summary`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.ok) setSummary(d.data); })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return null;
  if (summary.length === 0) return null;

  const totalMonthly = summary.reduce((acc, row) => {
    const cnt = parseInt(row.active_clients, 10) || 0;
    if (row.product_type === 'medsup') {
      // rough monthly: spread% × estimated premium; we don't have premiums, show $0 for now
      return acc;
    }
    const rate = parseFloat(row.override_renewal_amount) || 0;
    return acc + (cnt * rate);
  }, 0);

  return (
    <div className="mb-6 p-4 bg-gray-900 border border-gray-700 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-300">Estimated Monthly Agency Revenue</span>
        <span className="text-xs text-gray-500">Based on active client carrier matching</span>
      </div>
      <div className="flex flex-wrap gap-3">
        {summary.map(row => {
          const cnt = parseInt(row.active_clients, 10) || 0;
          let monthly = 0;
          if (row.product_type !== 'medsup') {
            monthly = cnt * (parseFloat(row.override_renewal_amount) || 0);
          }
          return (
            <div key={row.id} className="bg-gray-800 rounded-lg px-3 py-2 flex flex-col min-w-[120px]">
              <span className="text-xs text-gray-400 truncate">{row.carrier_name}</span>
              <span className="text-xs text-gray-500 capitalize">{row.product_type === 'medsup' ? 'Med Supp' : row.product_type.toUpperCase()}</span>
              <span className="text-sm font-bold text-amber-400 mt-1">
                {row.product_type === 'medsup' ? '—' : `$${monthly.toFixed(0)}/mo`}
              </span>
              <span className="text-xs text-gray-500">{cnt} active</span>
            </div>
          );
        })}
      </div>
      {totalMonthly > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-700 flex justify-between">
          <span className="text-sm text-gray-400">Total estimated monthly (MA/PDP overrides)</span>
          <span className="text-sm font-bold text-amber-400">${totalMonthly.toFixed(0)}/mo</span>
        </div>
      )}
      <p className="text-xs text-gray-600 mt-2">Approximate — based on client carrier field matching. Med Supp revenue requires premium data.</p>
    </div>
  );
}

// ── Payout Calculator ─────────────────────────────────────────────────────────
function PayoutCalculator({ configs }) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [premium, setPremium] = useState('150');
  const [selectedState, setSelectedState] = useState('');
  const [stateRates, setStateRates] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetch(`${BASE}/api/agency-setup/state-rates`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok) setStateRates(j.data || []); })
      .catch(() => {});
  }, [open]);

  const config = configs.find(c => c.id === selectedId);

  // Look up state override for the selected config's product type + state
  const stateOverride = config && selectedState && stateRates.find(
    r => r.product_type === config.product_type && r.state === selectedState
  );

  const calc = () => {
    if (!config) return null;
    if (config.product_type === 'medsup') {
      const p = parseFloat(premium) || 0;
      const annual = p * 12;
      const agentY1 = annual * ((parseFloat(config.agent_year1_pct) || 0) / 100);
      const agentY2 = annual * ((parseFloat(config.agent_years2_6_pct) || 0) / 100);
      const agencyY1 = annual * (((parseFloat(config.agency_contracted_pct) || 0) - (parseFloat(config.agent_year1_pct) || 0)) / 100);
      const agencyY2 = annual * (((parseFloat(config.agency_contracted_pct) || 0) - (parseFloat(config.agent_years2_6_pct) || 0)) / 100);
      const lifetime = agencyY1 + (5 * agencyY2);
      return { type: 'medsup', agentY1, agentY2, agencyY1, agencyY2, lifetime };
    } else {
      // Use state override amounts if available (these are the carrier-level rates paid to agents)
      const agentInit = stateOverride?.initial_amount != null
        ? parseFloat(stateOverride.initial_amount)
        : (parseFloat(config.agent_initial_amount) || 0);
      const agentRenew = stateOverride?.renewal_amount != null
        ? parseFloat(stateOverride.renewal_amount)
        : (parseFloat(config.agent_renewal_amount) || 0);
      const agencyInit = parseFloat(config.override_initial_amount) || 0;
      const agencyRenew = parseFloat(config.override_renewal_amount) || 0;
      return { type: 'ma', agentInit, agentRenew, agencyInit, agencyRenew, hasStateOverride: !!stateOverride };
    }
  };

  const result = calc();

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full mt-4 p-3 border border-dashed border-gray-700 rounded-xl text-gray-500 hover:border-amber-400 hover:text-amber-400 transition-colors text-sm flex items-center justify-center gap-2">
        <DollarSign size={14} /> Payout Calculator
      </button>
    );
  }

  return (
    <div className="mt-4 bg-gray-900 border border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-white">Payout Calculator</span>
        <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-300"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className={LABEL_CLS}>Select Carrier Config</label>
          <select className={INPUT_CLS} value={selectedId} onChange={e => { setSelectedId(e.target.value); setSelectedState(''); }}>
            <option value="">-- choose --</option>
            {configs.map(c => (
              <option key={c.id} value={c.id}>
                {c.carrier_name} ({c.product_type === 'medsup' ? 'Med Supp' : c.product_type.toUpperCase()})
              </option>
            ))}
          </select>
        </div>
        {config?.product_type === 'medsup' ? (
          <div>
            <label className={LABEL_CLS}>Monthly Premium ($)</label>
            <input type="number" className={INPUT_CLS} value={premium} onChange={e => setPremium(e.target.value)} min="0" />
          </div>
        ) : config && (
          <div>
            <label className={LABEL_CLS}>State (optional override)</label>
            <select className={INPUT_CLS} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
              <option value="">— National default —</option>
              {US_STATES.map(s => {
                const hasOverride = stateRates.some(r => r.state === s && r.product_type === config.product_type);
                return <option key={s} value={s}>{s}{hasOverride ? ' ★' : ''}</option>;
              })}
            </select>
          </div>
        )}
      </div>

      {result && (
        <div className="bg-amber-950/40 border border-amber-800/40 rounded-lg p-3">
          {result.type === 'ma' ? (
            <>
              {result.hasStateOverride && (
                <div className="text-xs text-amber-400 mb-2 flex items-center gap-1">
                  <AlertTriangle size={11} /> Using state override rates for {selectedState}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-400 mb-1 font-medium">New Enrollment (Year 1)</div>
                  <div className="text-gray-300">Agent: <span className="text-white font-semibold">${result.agentInit.toFixed(2)}</span></div>
                  <div className="text-gray-300">Agency override: <span className="text-amber-400 font-semibold">${result.agencyInit.toFixed(2)}</span></div>
                  <div className="text-gray-500 text-xs mt-1">Total: ${(result.agentInit + result.agencyInit).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1 font-medium">Renewal</div>
                  <div className="text-gray-300">Agent: <span className="text-white font-semibold">${result.agentRenew.toFixed(2)}</span></div>
                  <div className="text-gray-300">Agency override: <span className="text-amber-400 font-semibold">${result.agencyRenew.toFixed(2)}</span></div>
                  <div className="text-gray-500 text-xs mt-1">Total: ${(result.agentRenew + result.agencyRenew).toFixed(2)}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-400 mb-1 font-medium">Year 1</div>
                <div className="text-gray-300">Agent: <span className="text-white font-semibold">${result.agentY1.toFixed(2)}</span></div>
                <div className="text-gray-300">Agency: <span className="text-amber-400 font-semibold">${result.agencyY1.toFixed(2)}</span></div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1 font-medium">Years 2–6</div>
                <div className="text-gray-300">Agent: <span className="text-white font-semibold">${result.agentY2.toFixed(2)}/yr</span></div>
                <div className="text-gray-300">Agency: <span className="text-amber-400 font-semibold">${result.agencyY2.toFixed(2)}/yr</span></div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1 font-medium">6-yr Agency Total</div>
                <div className="text-amber-400 font-bold text-base">${result.lifetime.toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── State Rate Overrides ──────────────────────────────────────────────────────
function StateRateOverrides() {
  const [open, setOpen] = useState(false);
  const [productTab, setProductTab] = useState('ma');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingState, setEditingState] = useState(null); // state code
  const [form, setForm] = useState({ initial_amount: '', renewal_amount: '' });
  const [saving, setSaving] = useState(false);

  const MA_DEFAULT_INITIAL = 694;
  const MA_DEFAULT_RENEWAL = 347;
  const PDP_DEFAULT_INITIAL = 100;
  const PDP_DEFAULT_RENEWAL = 33;

  const defaults = productTab === 'ma'
    ? { initial: MA_DEFAULT_INITIAL, renewal: MA_DEFAULT_RENEWAL }
    : { initial: PDP_DEFAULT_INITIAL, renewal: PDP_DEFAULT_RENEWAL };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/state-rates`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setRates(j.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const getOverride = (state) => rates.find(r => r.product_type === productTab && r.state === state);

  const startEdit = (state) => {
    const ov = getOverride(state);
    setEditingState(state);
    setForm({ initial_amount: ov?.initial_amount || '', renewal_amount: ov?.renewal_amount || '' });
  };

  const cancelEdit = () => { setEditingState(null); setForm({ initial_amount: '', renewal_amount: '' }); };

  const save = async (state) => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/state-rates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify({ product_type: productTab, state, ...form }),
      });
      const j = await res.json();
      if (j.ok) { await load(); cancelEdit(); }
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (state) => {
    const ov = getOverride(state);
    if (!ov) return;
    await fetch(`${BASE}/api/agency-setup/state-rates/${ov.id}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
    load();
    if (editingState === state) cancelEdit();
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full mt-4 p-3 border border-dashed border-gray-700 rounded-xl text-gray-500 hover:border-amber-400 hover:text-amber-400 transition-colors text-sm flex items-center justify-center gap-2">
        <Globe size={14} /> State Rate Overrides
      </button>
    );
  }

  return (
    <div className="mt-4 bg-gray-900 border border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-sm font-semibold text-white">State Rate Overrides</span>
          <p className="text-xs text-gray-500 mt-0.5">Override national CMS defaults per state. Amber border = custom rate set.</p>
        </div>
        <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-300"><X size={14} /></button>
      </div>

      {/* MA / PDP tabs */}
      <div className="flex gap-1 bg-gray-800 p-1 rounded-lg w-fit mb-4">
        {['ma','pdp'].map(t => (
          <button key={t} onClick={() => { setProductTab(t); cancelEdit(); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${productTab === t ? 'bg-amber-400 text-gray-900' : 'text-gray-400 hover:text-white'}`}>
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      <p className="text-xs text-gray-600 mb-3">
        National default — Initial: ${defaults.initial} · Renewal: ${defaults.renewal}
      </p>

      {loading ? (
        <div className="text-gray-500 text-sm py-4 text-center">Loading...</div>
      ) : (
        <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-1.5">
          {US_STATES.map(state => {
            const ov = getOverride(state);
            const isOverridden = !!ov;
            const isEditing = editingState === state;

            return (
              <div key={state}>
                <button
                  onClick={() => isEditing ? cancelEdit() : startEdit(state)}
                  className={`w-full p-2 rounded-lg border text-center transition-colors ${
                    isOverridden
                      ? 'border-amber-600 bg-amber-900/20 text-amber-300'
                      : 'border-gray-800 bg-gray-800/50 text-gray-500 hover:border-gray-600 hover:text-gray-300'
                  } ${isEditing ? 'ring-2 ring-amber-400' : ''}`}
                >
                  <div className="text-xs font-bold">{state}</div>
                  {isOverridden && (
                    <div className="text-xs text-amber-400 leading-tight mt-0.5">
                      <div>${ov.initial_amount || '—'}</div>
                      <div>${ov.renewal_amount || '—'}</div>
                    </div>
                  )}
                </button>

                {isEditing && (
                  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={cancelEdit}>
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 w-72" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-white">{state} — {productTab.toUpperCase()} Override</h4>
                        <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-300"><X size={14} /></button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className={LABEL_CLS}>Initial Amount ($)</label>
                          <input className={INPUT_CLS} type="number" value={form.initial_amount}
                            onChange={e => setForm(f => ({ ...f, initial_amount: e.target.value }))}
                            placeholder={`Default $${defaults.initial}`} />
                        </div>
                        <div>
                          <label className={LABEL_CLS}>Renewal Amount ($)</label>
                          <input className={INPUT_CLS} type="number" value={form.renewal_amount}
                            onChange={e => setForm(f => ({ ...f, renewal_amount: e.target.value }))}
                            placeholder={`Default $${defaults.renewal}`} />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => save(state)} disabled={saving}
                          className="bg-amber-400 text-gray-900 font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-300 transition-colors text-xs flex items-center gap-1">
                          {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />} Save
                        </button>
                        {isOverridden && (
                          <button onClick={() => clearOverride(state)}
                            className="border border-red-800 text-red-400 px-3 py-1.5 rounded-lg text-xs hover:border-red-600 transition-colors">
                            Clear Override
                          </button>
                        )}
                        <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-300 text-xs ml-auto">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MA/PDP Edit Form ──────────────────────────────────────────────────────────
function MaPdpForm({ config, productType, onSave, onCancel }) {
  const defaults = CMS_DEFAULTS_2026[productType] || {};
  const [form, setForm] = useState({
    carrier_name:            config?.carrier_name            || '',
    agent_initial_amount:    config?.agent_initial_amount    ?? defaults.initial ?? '',
    agent_renewal_amount:    config?.agent_renewal_amount    ?? defaults.renewal ?? '',
    agent_pay_model:         config?.agent_pay_model         || 'direct',
    override_type:           config?.override_type           || 'flat_dollar',
    override_pct:            config?.override_pct            ?? '',
    override_initial_amount: config?.override_initial_amount ?? '',
    override_renewal_amount: config?.override_renewal_amount ?? '',
    override_tier:           config?.override_tier           || 'mga',
    payment_timing:          config?.payment_timing          || 'monthly',
    chargeback_months:       config?.chargeback_months       ?? 3,
    is_commissionable:       config?.is_commissionable       ?? true,
    notes:                   config?.notes                   || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const agentInit = parseFloat(form.agent_initial_amount) || 0;
  const agentRenew = parseFloat(form.agent_renewal_amount) || 0;
  const agencyInit = parseFloat(form.override_initial_amount) || 0;
  const agencyRenew = parseFloat(form.override_renewal_amount) || 0;

  return (
    <div className="mt-3 space-y-4">
      {!config && (
        <div>
          <label className={LABEL_CLS}>Carrier Name *</label>
          <input className={INPUT_CLS} value={form.carrier_name} onChange={e => set('carrier_name', e.target.value)} placeholder="e.g. Humana" />
        </div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Agent Commission</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>Initial $/enrollment</label>
            <input type="number" className={INPUT_CLS} value={form.agent_initial_amount} onChange={e => set('agent_initial_amount', e.target.value)} placeholder={String(defaults.initial || '')} />
          </div>
          <div>
            <label className={LABEL_CLS}>Renewal $/enrollment</label>
            <input type="number" className={INPUT_CLS} value={form.agent_renewal_amount} onChange={e => set('agent_renewal_amount', e.target.value)} placeholder={String(defaults.renewal || '')} />
          </div>
          <div>
            <label className={LABEL_CLS}>Agent Pay Model</label>
            <select className={INPUT_CLS} value={form.agent_pay_model} onChange={e => set('agent_pay_model', e.target.value)}>
              <option value="direct">Direct from carrier</option>
              <option value="through_agency">Through agency</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Agency Override</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>Override Type</label>
            <select className={INPUT_CLS} value={form.override_type} onChange={e => set('override_type', e.target.value)}>
              <option value="flat_dollar">Flat $/enrollment</option>
              <option value="percent_comp_base">% of comp base</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Tier</label>
            <select className={INPUT_CLS} value={form.override_tier} onChange={e => set('override_tier', e.target.value)}>
              {OVERRIDE_TIERS.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
            </select>
          </div>
          {form.override_type === 'percent_comp_base' ? (
            <div>
              <label className={LABEL_CLS}>Override %</label>
              <input type="number" className={INPUT_CLS} value={form.override_pct} onChange={e => set('override_pct', e.target.value)} step="0.01" />
            </div>
          ) : (
            <>
              <div>
                <label className={LABEL_CLS}>Override Initial $/enrollment</label>
                <input type="number" className={INPUT_CLS} value={form.override_initial_amount} onChange={e => set('override_initial_amount', e.target.value)} />
              </div>
              <div>
                <label className={LABEL_CLS}>Override Renewal $/enrollment</label>
                <input type="number" className={INPUT_CLS} value={form.override_renewal_amount} onChange={e => set('override_renewal_amount', e.target.value)} />
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Payment & Chargeback</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>Payment Timing</label>
            <select className={INPUT_CLS} value={form.payment_timing} onChange={e => set('payment_timing', e.target.value)}>
              <option value="monthly">Monthly (PMPM)</option>
              <option value="annual_advance">Annual Advance</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Chargeback Months</label>
            <input type="number" className={INPUT_CLS} value={form.chargeback_months} onChange={e => set('chargeback_months', e.target.value)} min="0" max="12" />
          </div>
          {productType === 'pdp' && (
            <div className="flex items-center gap-2 col-span-2">
              <input type="checkbox" id="is_comm" checked={form.is_commissionable} onChange={e => set('is_commissionable', e.target.checked)} className="rounded" />
              <label htmlFor="is_comm" className="text-sm text-gray-300">Commissionable (uncheck if carrier pays $0 override)</label>
            </div>
          )}
        </div>
      </div>

      {(agentInit > 0 || agencyInit > 0) && (
        <div className="bg-amber-950/40 border border-amber-800/40 rounded-lg p-3 text-sm">
          <div className="text-amber-400 font-semibold text-xs mb-1">Per-Enrollment Preview</div>
          <div className="text-gray-300">
            New: Agent <strong className="text-white">${agentInit}</strong> + Agency <strong className="text-amber-400">${agencyInit}</strong> = <strong className="text-white">${agentInit + agencyInit}</strong> total
          </div>
          <div className="text-gray-300 mt-0.5">
            Renewal: Agent <strong className="text-white">${agentRenew}</strong> + Agency <strong className="text-amber-400">${agencyRenew}</strong> = <strong className="text-white">${agentRenew + agencyRenew}</strong>/yr
          </div>
        </div>
      )}

      <div>
        <label className={LABEL_CLS}>Notes (optional)</label>
        <textarea className={INPUT_CLS} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>

      <div className="flex gap-2">
        <button className={BTN_PRIMARY} onClick={() => onSave({ ...form, product_type: productType })}>Save</button>
        <button className={BTN_SECONDARY} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Med Supp Edit Form ────────────────────────────────────────────────────────
function MedSuppForm({ config, onSave, onCancel }) {
  const [form, setForm] = useState({
    carrier_name:           config?.carrier_name           || '',
    agency_contracted_pct:  config?.agency_contracted_pct  ?? '',
    agent_year1_pct:        config?.agent_year1_pct        ?? '',
    agent_years2_6_pct:     config?.agent_years2_6_pct     ?? '',
    agent_year7plus_pct:    config?.agent_year7plus_pct    ?? 0,
    comp_base:              config?.comp_base              || 'original_premium',
    advance_months:         config?.advance_months         ?? 12,
    renewal_years:          config?.renewal_years          ?? 6,
    commission_structure:   config?.commission_structure   || 'hybrid',
    notes:                  config?.notes                  || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const contracted = parseFloat(form.agency_contracted_pct) || 0;
  const y1 = parseFloat(form.agent_year1_pct) || 0;
  const y2_6 = parseFloat(form.agent_years2_6_pct) || 0;
  const spreadY1 = contracted - y1;
  const spreadY2_6 = contracted - y2_6;
  const samplePremium = 150;
  const spreadY1Annual = samplePremium * 12 * (spreadY1 / 100);
  const spreadY2Annual = samplePremium * 12 * (spreadY2_6 / 100);
  const lifetime = spreadY1Annual + 5 * spreadY2Annual;

  return (
    <div className="mt-3 space-y-4">
      {!config && (
        <div>
          <label className={LABEL_CLS}>Carrier Name *</label>
          <input className={INPUT_CLS} value={form.carrier_name} onChange={e => set('carrier_name', e.target.value)} placeholder="e.g. Mutual of Omaha" />
        </div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Carrier Rates</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>Agency Contracted %</label>
            <input type="number" className={INPUT_CLS} value={form.agency_contracted_pct} onChange={e => set('agency_contracted_pct', e.target.value)} step="0.01" placeholder="22.00" />
          </div>
          <div>
            <label className={LABEL_CLS}>Agent Year 1 %</label>
            <input type="number" className={INPUT_CLS} value={form.agent_year1_pct} onChange={e => set('agent_year1_pct', e.target.value)} step="0.01" placeholder="20.00" />
          </div>
          <div>
            <label className={LABEL_CLS}>Agent Years 2–6 %</label>
            <input type="number" className={INPUT_CLS} value={form.agent_years2_6_pct} onChange={e => set('agent_years2_6_pct', e.target.value)} step="0.01" placeholder="10.00" />
          </div>
          <div>
            <label className={LABEL_CLS}>Agent Year 7+ %</label>
            <input type="number" className={INPUT_CLS} value={form.agent_year7plus_pct} onChange={e => set('agent_year7plus_pct', e.target.value)} step="0.01" placeholder="0" />
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Comp Base</div>
        <div className="flex gap-4">
          {[
            { value: 'original_premium', label: 'Original Premium', desc: 'Locked at sale price' },
            { value: 'current_premium', label: 'Current Premium', desc: 'Grows with rate hikes' },
          ].map(opt => (
            <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="comp_base" value={opt.value} checked={form.comp_base === opt.value} onChange={() => set('comp_base', opt.value)} className="mt-0.5" />
              <div>
                <div className="text-sm text-gray-300">{opt.label}</div>
                <div className="text-xs text-gray-500">{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Payment</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>Payment Method</label>
            <select className={INPUT_CLS} value={form.advance_months} onChange={e => set('advance_months', parseInt(e.target.value))}>
              <option value={12}>12-month advance</option>
              <option value={1}>As-earned monthly</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Renewal Years</label>
            <input type="number" className={INPUT_CLS} value={form.renewal_years} onChange={e => set('renewal_years', e.target.value)} min="1" max="20" />
          </div>
        </div>
      </div>

      {contracted > 0 && (
        <div className="bg-amber-950/40 border border-amber-800/40 rounded-lg p-3 text-sm space-y-1">
          <div className="text-amber-400 font-semibold text-xs mb-1">Agency Spread (on $150/mo sample premium)</div>
          <div className="text-gray-300">Y1 spread: <strong className="text-white">{spreadY1.toFixed(2)}%</strong> · <strong className="text-amber-400">${spreadY1Annual.toFixed(0)}/yr</strong> retained</div>
          <div className="text-gray-300">Y2–6 spread: <strong className="text-white">{spreadY2_6.toFixed(2)}%</strong> · <strong className="text-amber-400">${spreadY2Annual.toFixed(0)}/yr</strong> retained</div>
          <div className="text-gray-400 text-xs">6-yr lifetime value per policy: <strong className="text-amber-400">${lifetime.toFixed(0)}</strong></div>
        </div>
      )}

      <div>
        <label className={LABEL_CLS}>Notes (optional)</label>
        <textarea className={INPUT_CLS} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>

      <div className="flex gap-2">
        <button className={BTN_PRIMARY} onClick={() => onSave({ ...form, product_type: 'medsup' })}>Save</button>
        <button className={BTN_SECONDARY} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Carrier Commission Card ───────────────────────────────────────────────────
function CarrierCommissionCard({ config, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveCard = async (formData) => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/commissions/${config.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        body: JSON.stringify(formData),
      });
      const d = await res.json();
      if (d.ok) { onUpdate(d.data); setExpanded(false); }
    } finally {
      setSaving(false);
    }
  };

  const summary = () => {
    const p = config.product_type;
    if (p === 'medsup') {
      const contracted = parseFloat(config.agency_contracted_pct) || 0;
      const y1 = parseFloat(config.agent_year1_pct) || 0;
      const y2 = parseFloat(config.agent_years2_6_pct) || 0;
      return `${contracted}% contracted · Agent ${y1}% Y1 / ${y2}% Y2–6 · You keep ${(contracted-y1).toFixed(1)}% Y1 / ${(contracted-y2).toFixed(1)}% Y2–6`;
    }
    if (!config.is_commissionable) return 'Non-commissionable';
    const ai = parseFloat(config.agent_initial_amount) || 0;
    const ar = parseFloat(config.agent_renewal_amount) || 0;
    const oi = parseFloat(config.override_initial_amount) || 0;
    const or_ = parseFloat(config.override_renewal_amount) || 0;
    const tier = (config.override_tier || 'mga').toUpperCase();
    return `$${ai} initial / $${ar} renewal · Override $${oi} initial / $${or_} renewal · ${tier} tier`;
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">{config.carrier_name}</div>
          <div className="text-xs text-gray-400 truncate mt-0.5">{summary()}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this commission config?')) onDelete(config.id); }} className="text-gray-600 hover:text-red-400 transition-colors p-1"><Trash2 size={13} /></button>
          {expanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-700">
          {config.product_type === 'medsup'
            ? <MedSuppForm config={config} onSave={saving ? () => {} : handleSaveCard} onCancel={() => setExpanded(false)} />
            : <MaPdpForm config={config} productType={config.product_type} onSave={saving ? () => {} : handleSaveCard} onCancel={() => setExpanded(false)} />
          }
        </div>
      )}
    </div>
  );
}

// ── Add Carrier Form ──────────────────────────────────────────────────────────
function AddCarrierForm({ productType, onSave, onCancel }) {
  const [saving, setSaving] = useState(false);

  const handleSave = async (formData) => {
    if (!formData.carrier_name?.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/commissions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify(formData),
      });
      const d = await res.json();
      if (d.ok) onSave(d.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 border border-dashed border-amber-600/50 rounded-lg p-3">
      <div className="text-xs font-semibold text-amber-400 mb-3">New Carrier</div>
      {productType === 'medsup'
        ? <MedSuppForm config={null} onSave={saving ? () => {} : handleSave} onCancel={onCancel} />
        : <MaPdpForm config={null} productType={productType} onSave={saving ? () => {} : handleSave} onCancel={onCancel} />
      }
    </div>
  );
}

// ── Main Commission Section ───────────────────────────────────────────────────
function CommissionSection({ onSaved }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ma');
  const [adding, setAdding] = useState(false);
  const [summaryKey, setSummaryKey] = useState(0);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/commissions`, { credentials: 'include' });
      const d = await res.json();
      if (d.ok) setConfigs(d.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleAdd = (newConfig) => {
    setConfigs(prev => [...prev.filter(c => c.id !== newConfig.id), newConfig]);
    setAdding(false);
    setSummaryKey(k => k + 1);
    onSaved('Commission config saved');
  };

  const handleUpdate = (updated) => {
    setConfigs(prev => prev.map(c => c.id === updated.id ? updated : c));
    setSummaryKey(k => k + 1);
    onSaved('Commission config updated');
  };

  const handleDelete = async (id) => {
    const res = await fetch(`${BASE}/api/agency-setup/commissions/${id}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
    const d = await res.json();
    if (d.ok) {
      setConfigs(prev => prev.filter(c => c.id !== id));
      setSummaryKey(k => k + 1);
      onSaved('Config deleted', 'info');
    }
  };

  const tabConfigs = configs.filter(c => c.product_type === activeTab);

  return (
    <div>
      <SectionHeader
        title="Commission Structures"
        description="Configure what carriers pay you, what you pay your agents, and your agency spread. Used for payout calculations and revenue forecasting."
      />

      <RevenueSummaryBar refreshKey={summaryKey} />

      {/* Product tab bar */}
      <div className="flex gap-1 mb-4 bg-gray-800 p-1 rounded-lg w-fit">
        {PRODUCT_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setAdding(false); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-amber-400 text-gray-900' : 'text-gray-400 hover:text-white'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">Loading...</div>
      ) : (
        <div className="space-y-2">
          {tabConfigs.length === 0 && !adding && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No {PRODUCT_TABS.find(t => t.key === activeTab)?.label} configs yet.
            </div>
          )}

          {tabConfigs.map(config => (
            <CarrierCommissionCard
              key={config.id}
              config={config}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}

          {adding && (
            <AddCarrierForm
              productType={activeTab}
              onSave={handleAdd}
              onCancel={() => setAdding(false)}
            />
          )}

          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="w-full py-3 border border-dashed border-gray-700 rounded-lg text-gray-500 hover:border-amber-400 hover:text-amber-400 transition-colors text-sm flex items-center justify-center gap-2"
            >
              <Plus size={14} /> Add Carrier
            </button>
          )}
        </div>
      )}

      <PayoutCalculator configs={configs} />
      <StateRateOverrides />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function AgencySetup() {
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('profile');
  const [score, setScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const loadScore = useCallback(async () => {
    setScoreLoading(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/health-score`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setScore(j.data);
    } catch (e) { /* silent */ }
    finally { setScoreLoading(false); }
  }, []);

  const refreshScore = async () => {
    setScoreLoading(true);
    try {
      const res = await fetch(`${BASE}/api/agency-setup/health-score/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...csrfHeadersFor('POST') }
      });
      const j = await res.json();
      if (j.ok) { setScore(j.data); showToast('Health score refreshed', 'info'); }
    } catch (e) { /* silent */ }
    finally { setScoreLoading(false); }
  };

  // Check wizard state on mount
  useEffect(() => {
    async function checkWizard() {
      try {
        const res = await fetch(`${BASE}/api/agency-setup/wizard`, { credentials: 'include' });
        const j = await res.json();
        if (j.ok && j.data && !j.data.wizard_completed) {
          setShowWizard(true);
        }
      } catch (e) {
        // No agency or API not available — show main view
      }
      setWizardChecked(true);
    }
    checkWizard();
    loadScore();
  }, [loadScore]);

  const handleSaved = useCallback((msg, type = 'success') => {
    showToast(msg, type);
    // Refresh health score after any save
    setTimeout(() => loadScore(), 500);
  }, [showToast, loadScore]);

  const handleWizardComplete = () => {
    setShowWizard(false);
    loadScore();
  };

  if (!wizardChecked) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (showWizard) {
    return (
      <SetupWizard
        onComplete={handleWizardComplete}
        onSkip={() => setShowWizard(false)}
      />
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':     return <ProfileSection onSaved={handleSaved} />;
      case 'hours':       return <BusinessHoursSection onSaved={handleSaved} />;
      case 'team':        return <TeamSection onSaved={handleSaved} />;
      case 'carriers':    return <CarriersSection onSaved={handleSaved} />;
      case 'fmo':         return <FMOSection onSaved={handleSaved} />;
      case 'commissions': return <CommissionSection onSaved={handleSaved} />;
      case 'automations': return <AutomationsSection />;
      case 'compliance':  return <ComplianceSection onSaved={handleSaved} />;
      case 'integrations':return <IntegrationsSection />;
      case 'billing':     return <BillingSection />;
      default:            return <ProfileSection onSaved={handleSaved} />;
    }
  };

  const getScoreForSection = (scoreKey) => {
    if (!scoreKey || !score) return null;
    return score[scoreKey] ?? null;
  };

  const navItem = (section) => {
    const Icon = section.icon;
    const sectionScore = getScoreForSection(section.scoreKey);
    const isActive = activeSection === section.id;
    const scoreColor = sectionScore === null ? '' : sectionScore >= 16 ? 'text-emerald-400' : sectionScore >= 8 ? 'text-amber-400' : 'text-red-400';

    return (
      <button
        key={section.id}
        onClick={() => { setActiveSection(section.id); setSidebarOpen(false); }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
          isActive
            ? 'bg-amber-400/10 text-amber-400 border border-amber-400/20'
            : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
        }`}
      >
        <Icon size={16} className="flex-shrink-0" />
        <span className="flex-1">{section.label}</span>
        {sectionScore !== null && (
          <span className={`text-xs tabular-nums font-medium ${scoreColor}`}>{sectionScore}/20</span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Top bar */}
      <div className="border-b border-gray-800 px-4 sm:px-6 py-4 flex items-center gap-3 bg-gray-950 sticky top-0 z-30">
        <button
          onClick={() => navigate('/agency/dashboard')}
          className="text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1 text-sm"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>
        <span className="text-gray-700">/</span>
        <h1 className="text-white font-semibold">Agency Setup</h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="ml-auto lg:hidden text-gray-500 hover:text-gray-300"
        >
          <Menu size={20} />
        </button>
      </div>

      <div className="flex flex-1">
        {/* Left rail — mobile overlay, desktop fixed */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-gray-950 border-r border-gray-800 flex flex-col
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `} style={{ top: '57px' }}>
          {/* Mobile close */}
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden absolute top-3 right-3 text-gray-500">
            <X size={18} />
          </button>

          <div className="flex-1 overflow-y-auto p-3 pt-4">
            <nav className="space-y-0.5">
              {SECTIONS.map(navItem)}
            </nav>
          </div>

          {/* Health score in rail */}
          <div className="p-3 border-t border-gray-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 font-medium">Health Score</span>
              <span className={`text-sm font-bold tabular-nums ${
                (score?.totalScore ?? 0) >= 80 ? 'text-emerald-400' :
                (score?.totalScore ?? 0) >= 60 ? 'text-amber-400' : 'text-red-400'
              }`}>{score?.totalScore ?? '--'}/100</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  (score?.totalScore ?? 0) >= 80 ? 'bg-emerald-400' :
                  (score?.totalScore ?? 0) >= 60 ? 'bg-amber-400' : 'bg-red-500'
                }`}
                style={{ width: `${score?.totalScore ?? 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">

            {/* Health score widget — always visible at top */}
            <HealthScoreWidget
              score={score}
              loading={scoreLoading}
              onRefresh={refreshScore}
              onRelaunchWizard={() => setShowWizard(true)}
            />

            {/* Section content */}
            <div className="relative pb-20">
              {renderSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
